import React, { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ProfileImageLightboxProps {
  isOpen: boolean;
  imageSrc: string | null;
  alt: string;
  onClose: () => void;
}

const getFocusableElements = (root: HTMLElement | null): HTMLElement[] => {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => !element.hasAttribute('disabled') && element.tabIndex !== -1);
};

const ProfileImageLightbox: React.FC<ProfileImageLightboxProps> = ({
  isOpen,
  imageSrc,
  alt,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current =
      typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(dialogRef.current);
      if (focusableElements.length === 0) {
        event.preventDefault();
        closeButtonRef.current?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => previousFocusRef.current?.focus());
    };
  }, [isOpen, onClose]);

  if (!isOpen || !imageSrc) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/86 p-3 [padding-bottom:calc(0.75rem+env(safe-area-inset-bottom))] sm:p-5"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Podglad zdjecia profilu serwisu"
        className="relative flex h-full max-h-[calc((var(--app-vh,1vh)*100)-24px-env(safe-area-inset-bottom))] w-full max-w-[min(1120px,calc(100vw-24px))] items-center justify-center overflow-hidden rounded-lg outline-none sm:max-h-[calc((var(--app-vh,1vh)*100)-40px-env(safe-area-inset-bottom))] sm:max-w-[min(1120px,calc(100vw-40px))]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/18 bg-black/58 text-white shadow-dropdown transition-colors hover:bg-black/78 focus-outline sm:right-3 sm:top-3"
          aria-label="Zamknij podglad zdjecia"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>

        <img
          src={imageSrc}
          alt={alt}
          className="max-h-full max-w-full object-contain"
          draggable={false}
        />
      </div>
    </div>
  );
};

export default ProfileImageLightbox;
